import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Home, Gift, Upload } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import FortuneWheel from './components/FortuneWheel/FortuneWheel'
import PrizesModal from './components/PrizesModal/PrizesModal'
import Logo from '../../components/Logo/Logo'
import FortuneLoadingScreen from '../../components/FortuneLoadingScreen/FortuneLoadingScreen'
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp'
import {
  useCanSpin,
  useSpinPrizes,
  useMyPrizes,
  useSpin,
  useNextDay,
  useResetRewards,
} from '../../hooks/useApi'
import { SpinPrizePublic } from '../../types/api'
import {
  shareToTelegramStory,
  downloadStoryImage,
} from '../../utils/telegramStories'
import './FortuneWheelPage.css'
import konfetiMore from '../../assets/images/konfetiMore.png'
const FortuneWheelPage = () => {
  const navigate = useNavigate()
  useTelegramWebApp() // Инициализируем Telegram WebApp
  const [isSpinning, setIsSpinning] = useState(false)
  const [winningIndex, setWinningIndex] = useState<number | null>(null)
  const [showPrizesModal, setShowPrizesModal] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [showLoadingScreen, setShowLoadingScreen] = useState(false)
  const [wonPrize, setWonPrize] = useState<SpinPrizePublic | null>(() => {
    // Восстанавливаем выигранный приз из sessionStorage
    const saved = sessionStorage.getItem('lastWonPrize')
    return saved ? JSON.parse(saved) : null
  })
  const [spinError, setSpinError] = useState<string | null>(null)

  const { data: canSpinData, isLoading: isLoadingCanSpin } = useCanSpin()
  const { data: prizesData, isLoading: isLoadingPrizes } = useSpinPrizes()
  const { data: myPrizesData, refetch: refetchMyPrizes } = useMyPrizes()
  const spinMutation = useSpin()
  const nextDayMutation = useNextDay()
  const resetRewardsMutation = useResetRewards()

  const canSpin = canSpinData?.can_spin ?? false
  const spinReason = canSpinData?.reason
  const sectors = prizesData?.sectors ?? []
  const myPrizes = myPrizesData?.prizes ?? []

  // Устанавливаем красный фон Telegram и кнопку "Назад"
  useEffect(() => {
    // Устанавливаем красный фон для body
    document.body.style.backgroundColor = '#7AA9C7'

    const tg = window.Telegram?.WebApp

    if (tg) {
      // Устанавливаем красный фон
      tg.headerColor = '#7AA9C7'
      tg.backgroundColor = '#7AA9C7'

      // Создаем обработчик кнопки "Назад"
      const backHandler = () => {
        console.log('FortuneWheel: Back button clicked, navigating to /')
        navigate('/')
      }

      // Скрываем и показываем кнопку заново, чтобы сбросить все обработчики
      tg.BackButton.hide()

      // Небольшая задержка, чтобы Telegram успел обработать hide
      setTimeout(() => {
        // Добавляем наш обработчик
        tg.BackButton.onClick(backHandler)
        // Показываем кнопку
        tg.BackButton.show()
      }, 50)

      // При размонтировании возвращаем обычный цвет и скрываем кнопку
      return () => {
        // Возвращаем стандартный цвет body
        document.body.style.backgroundColor = ''

        tg.headerColor = '#7AB5DB'
        tg.backgroundColor = '#7AB5DB'

        // Удаляем обработчик и скрываем кнопку
        if (tg.BackButton.offClick) {
          tg.BackButton.offClick(backHandler)
        }
        tg.BackButton.hide()
      }
    } else {
      // Если нет Telegram WebApp, просто очищаем фон при размонтировании
      return () => {
        document.body.style.backgroundColor = ''
      }
    }
  }, [navigate])

  // Блокируем прокрутку когда открыты модалки
  useEffect(() => {
    if (showPrizesModal || showResultModal) {
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
    } else {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
    }
  }, [showPrizesModal, showResultModal])

  const handleSpin = async () => {
    if (!canSpin || isSpinning || sectors.length === 0) {
      return
    }

    try {
      setIsSpinning(true)
      setSpinError(null) // Очищаем предыдущие ошибки
      // Очищаем флаг о получении приза при новом спине
      localStorage.removeItem('prizeCollected')
      const result = await spinMutation.mutateAsync()

      // Запрос успешен - крутим колесо
      // Сортируем секторы так же, как в компоненте FortuneWheel
      const sortedSectors = [...sectors].sort((a, b) => a.sort - b.sort)

      // Ищем сектор по spin_sector_id === spin_prize_id
      const winIndex = sortedSectors.findIndex(
        sector => sector.spin_sector_id === result.prize.spin_sector_id
      )

      if (winIndex !== -1) {
        setWinningIndex(winIndex)
        setWonPrize(result.prize)
        // Сохраняем в sessionStorage
        sessionStorage.setItem('lastWonPrize', JSON.stringify(result.prize))
      } else {
        // Если не нашли, выбираем случайный
        const randomIndex = Math.floor(Math.random() * sortedSectors.length)
        setWinningIndex(randomIndex)
        setWonPrize(result.prize)
        sessionStorage.setItem('lastWonPrize', JSON.stringify(result.prize))
        console.warn(
          '⚠️ Сектор не найден, используем случайный индекс:',
          randomIndex
        )
      }
    } catch (error: any) {
      console.error('Ошибка при спине:', error)
      setIsSpinning(false)

      // Получаем сообщение об ошибке
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        'Произошла ошибка при вращении колеса. Попробуйте еще раз.'
      setSpinError(errorMessage)
    }
  }

  const handleSpinComplete = () => {
    setIsSpinning(false)
    setShowResultModal(true)
    setSpinError(null) // Очищаем ошибку при успешном завершении
    refetchMyPrizes()
  }

  const handleGoHome = () => {
    navigate('/')
  }

  const handleShowPrizes = () => {
    setShowPrizesModal(true)
  }

  const handleResetLimit = async () => {
    try {
      await nextDayMutation.mutateAsync()
    } catch (error) {
      console.error('Не удалось сбросить лимит:', error)
    }
  }

  const handleResetRewards = async () => {
    try {
      await resetRewardsMutation.mutateAsync()
    } catch (error) {
      console.error('Не удалось сбросить награды:', error)
    }
  }

  const handleCloseResultModal = () => {
    setShowResultModal(false)
    // Отмечаем, что приз был забран
    localStorage.setItem('prizeCollected', 'true')
    // НЕ очищаем wonPrize и winningIndex, чтобы они оставались на экране

    const botUrl = 'https://t.me/pauline_secret_bot'
    const tg = window.Telegram?.WebApp
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(botUrl)
    } else if (tg?.openLink) {
      tg.openLink(botUrl)
    } else {
      window.open(botUrl, '_blank')
    }
  }

  const handleShareToStory = async () => {
    if (!wonPrize) return

    try {
      await shareToTelegramStory(wonPrize)
    } catch (error) {
      console.error('Ошибка при публикации в сторис:', error)
      // Fallback: предлагаем скачать изображение
      try {
        await downloadStoryImage(wonPrize)
        alert(
          'Изображение скачано! Теперь вы можете опубликовать его в Telegram Stories вручную.'
        )
      } catch (downloadError) {
        console.error('Ошибка при скачивании:', downloadError)
        alert('Не удалось создать изображение для сторис. Попробуйте еще раз.')
      }
    }
  }

  const handleOpenTerms = () => {
    const termsUrl =
      'https://docs.google.com/document/d/1mxxkKYT6Er5eLBCMN3oWy5vPjvIbdZpqTaxfYUu2vtQ/edit?tab=t.0'

    const tg = window.Telegram?.WebApp
    if (tg) {
      // Используем Telegram WebApp API для открытия ссылки
      tg.openLink(termsUrl)
    } else {
      // Fallback для случая, если WebApp недоступен
      window.open(termsUrl, '_blank')
    }
  }

  return (
    <>
      <AnimatePresence>
        {showLoadingScreen && (
          <FortuneLoadingScreen
            isLoading={showLoadingScreen}
            onComplete={() => setShowLoadingScreen(false)}
          />
        )}
      </AnimatePresence>

      <div className="fortune-wheel-page">
        <div className="fortune-wheel-scroll-container">
          {/* Main Content Card */}
          <div className="fortune-main-card">
            {/* Header Navigation */}
            <div className="fortune-header-nav">
              {/* Reset Limit Button (Dev) */}
              <div className="dev">
                <button onClick={handleResetLimit} disabled={isSpinning}>
                  лимит
                </button>

                <button onClick={handleResetRewards} disabled={isSpinning}>
                  награды
                </button>
              </div>
              <div className="dev">
                <button className="nav-icon-button" onClick={handleGoHome}>
                  <Home size={24} color="#7AA9C7" />
                </button>
                <button className="nav-icon-button" onClick={handleShowPrizes}>
                  <Gift size={24} color="#7AA9C7" />
                </button>
              </div>
            </div>
            {/* Title Section */}
            <div className="fortune-title-section">
              <h1 className="fortune-main-title">SECRET WHEEL</h1>
              <p className="fortune-main-subtitle">
                Крути колесо и выигрывай ценные призы
              </p>
            </div>

            {/* Wheel Section */}
            <div className="fortune-wheel-section">
              {isLoadingPrizes ? (
                <div className="fortune-wheel-loading">
                  <p>Загружаем призы...</p>
                </div>
              ) : (
                <FortuneWheel
                  sectors={sectors}
                  isSpinning={isSpinning}
                  winningIndex={winningIndex}
                  onSpinComplete={handleSpinComplete}
                />
              )}
            </div>
            <div className="fortune-info-section">
              {/* Spin Button */}
              <button
                className="fortune-spin-button-main"
                onClick={handleSpin}
                disabled={!canSpin || isSpinning || isLoadingCanSpin}
              >
                {isSpinning ? 'Крутим...' : 'Крутить'}
              </button>

              {/* Error Message */}
              {spinError && (
                <p className="fortune-info-small error">{spinError}</p>
              )}

              {/* Info Text */}
              {!spinError && (
                <>
                  {isLoadingCanSpin ? (
                    <p className="fortune-info-small">
                      Проверяем возможность крутить...
                    </p>
                  ) : !canSpin ? (
                    wonPrize && !isSpinning && !showResultModal ? (
                      <p className="fortune-info-small">
                        {spinReason ||
                          'Сейчас прокрутить колесо нельзя. Попробуйте позже.'}
                      </p>
                    ) : null
                  ) : (
                    <p className="fortune-info-small">
                      Нажимая "Крутить", я соглашаюсь с{' '}
                      <span onClick={handleOpenTerms}> Условиями акции</span>
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Secret Chat Section */}
            <div className="fortune-chat-section">
              <Logo color="#7AA9C7" className="chat-logo" width={157} />
            </div>
          </div>
        </div>

        <PrizesModal
          isOpen={showPrizesModal}
          onClose={() => setShowPrizesModal(false)}
          wonPrizes={myPrizes}
          availablePrizes={sectors}
        />

        <AnimatePresence>
          {showResultModal && wonPrize && (
            <>
              <motion.div
                className="result-modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseResultModal}
              />
              <motion.div
                className="result-modal"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="result-modal-content">
                  <img
                    src={konfetiMore}
                    alt={wonPrize.header_text}
                    className="result-prize-konfeti"
                  />
                  <h2 className="result-title"> Поздравляем!</h2>
                  <span className="result-title-span">Вы выиграли </span>
                  <div className="result-prize-icon-container">
                    <img
                      src={wonPrize.icon_url}
                      alt={wonPrize.header_text}
                      className="result-prize-icon"
                    />
                  </div>
                  <h3 className="result-prize-name">
                    {wonPrize.detail_header_text}
                  </h3>
                  <p className="result-prize-name">{wonPrize.detail_text}</p>
                  <div className="result-prize-name-block">
                    <button
                      className="result-close-button fortune-spin-button-main"
                      onClick={handleCloseResultModal}
                    >
                      Забрать приз!
                    </button>
                    <button
                      className="result-storis-button"
                      onClick={handleShareToStory}
                    >
                      <Upload size={14} /> Поделиться в сторис
                    </button>
                    {wonPrize.detail_description && (
                      <p className="result-prize-description">
                        {wonPrize.detail_description}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

export default FortuneWheelPage
